import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Link from '@material-ui/core/Link';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import DescriptionIcon from '@material-ui/icons/Description';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VideocamIcon from '@material-ui/icons/Videocam';
import classNames from 'classnames';
import debounce from 'lodash.debounce';

import {
  coach,
  guest,
  verifiedRoomModerator,
} from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import BroadcasterNotesStore from 'stores/BroadcasterNotesStore';
import PeekStore from 'stores/PeekStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import TokenHolderIndicatorAvatar from 'components/TokenHolderIndicatorAvatar';
import ImageIcon from 'components/ChatMessageTypes/components/ImageIcon';
import routeDefinitions from 'enum/routeDefinitions';
import useContextualMenu from 'hooks/useContextualMenu';

import Fanclub from '../../../../../../../../images/fanclub-icon.png';
import GoldBadge from '../../../../../../../../images/user/gold-star.png';
import ContextualMenu from '../ContextualMenu/ContextualMenu';
import styles from '../UserListTab.css';


const waitDebounce = 300;

// eslint-disable-next-line complexity
function UserListItem(props) {
  const {
    accessLevel,
    additionalElement,
    handleDrawerClose,
    hasTokens,
    isBroadcasterChat,
    isBroadcasting,
    isFanclubSubscribed,
    isFavouritesTab,
    isFriendsTab,
    isRoomModerator,
    isSilenced,
    isStarred,
    isThePerformer,
    isUserList,
    originalUsername,
    profileImage,
    recentSpendingTier,
    topTipperItem,
    username,
  } = props;

  const { performerDetails: { username: performerName } } = ProfileStore;
  const { requestPeekStream, openedPeekWindows, requestedPeekWindows, closePeekWindow } = PeekStore;
  const { performerTopTipper } = TippingStore;
  const { hasAdminPrivileges } = UserStore;
  const { allNotes, getSingleNote } = BroadcasterNotesStore;
  const { showMenu, anchorEl, openMenu, closeMenu } = useContextualMenu();

  const iAmUserListMember =
    originalUsername?.toString().toLowerCase() === username?.toString().toLowerCase();
  const canSeeGuestContextMenu = accessLevel === guest && hasAdminPrivileges;
  const canSeeContextMenu = (!iAmUserListMember && accessLevel !== guest) || canSeeGuestContextMenu;

  const isPerformer = isThePerformer ||
    performerName?.toString().toLowerCase() === originalUsername?.toString().toLowerCase();
  const ableToPeek =
    !iAmUserListMember &&
    !isPerformer &&
    (isBroadcasting.toString() === 'm' || isBroadcasting === true);
  const handlePeek = debounce(() => {
    if (ableToPeek) {
      const alreadyOpened = openedPeekWindows.find(
        (peek) =>
          peek.performerName?.toLowerCase() === originalUsername?.toLowerCase()
      );
      const alreadyRequested = requestedPeekWindows[username];

      if (alreadyOpened) {
        closePeekWindow(originalUsername);
      } else if (!alreadyRequested) {
        requestPeekStream(originalUsername);
      }
    }
  }, waitDebounce);

  useEffect(() => {
    if (
      openedPeekWindows.find((peek) => peek.performerName.toLowerCase() === originalUsername.toLowerCase()) &&
      !ableToPeek
    ) {
      // close peek stream if stream is ended
      closePeekWindow(originalUsername);
    }
  }, [ableToPeek, closePeekWindow, openedPeekWindows, originalUsername]);

  const openContextualMenu = useCallback((e) => {
    openMenu(e);
  }, [openMenu]);

  const isFriendsOrFavoritesTab = isFriendsTab || isFavouritesTab;
  const showFanclubIcon = !isFriendsOrFavoritesTab && Boolean(parseInt(isFanclubSubscribed, 10));

  const isTipLeader =
    !isFriendsOrFavoritesTab &&
    accessLevel !== verifiedRoomModerator &&
    accessLevel !== coach &&
    !(parseInt(isRoomModerator, 10) === 1) &&
    performerTopTipper?.toLowerCase() === originalUsername?.toLowerCase();

  const modLabelWrapper = classNames({
    [styles.mod]: !isBroadcasterChat,
    [styles.modBroadcaster]: isBroadcasterChat,
  });

  const handleNoteModal = useCallback(() => {
    if (typeof handleDrawerClose === 'function') {
      handleDrawerClose();
    }
    getSingleNote(originalUsername);
  }, [originalUsername, getSingleNote, handleDrawerClose]);

  const handleUsernameClick = useCallback(
    (e) => {
      if (topTipperItem) {
        e.preventDefault();
        openMenu(e);
      }
    },
    [topTipperItem, openMenu]
  );

  const usernameClasses = classNames({
    [styles.Username]: true,
    [styles.UsernameAlt]: topTipperItem,
  });

  const url = topTipperItem ? '#' : routeDefinitions.get(routeDefinitions.profileV2.root, { user: originalUsername });

  const goldBadge = useMemo(() => {
    if (!isStarred || isFriendsOrFavoritesTab) {
      return null;
    }

    if (isBroadcasterChat) {
      return (
        <Tooltip
          arrow={true}
          placement="bottom"
          title={translate('v2broadcast.gold.user')}
        >
          <img
            alt={translate('v2profile.chat.gold.badge')}
            className={styles.GoldBadge}
            src={GoldBadge}
          />
        </Tooltip>
      );
    }

    return (
      <img
        alt={translate('v2profile.chat.gold.badge')}
        className={styles.GoldBadge}
        src={GoldBadge}
      />
    );
  }, [isBroadcasterChat, isFriendsOrFavoritesTab, isStarred]);

  const fanclubBadge = useMemo(() => {
    if (!showFanclubIcon) {
      return null;
    }

    return (
      <Tooltip
        arrow={true}
        placement="bottom"
        title={translate('profile.profile.fanclub.member')}
      >
        <img alt="fanclub star" className={styles.fanclubStar} src={Fanclub} />
      </Tooltip>
    );
  }, [showFanclubIcon]);

  const willDisplayIndicator = (
    isBroadcasterChat &&
    !isFriendsOrFavoritesTab &&
    !isPerformer &&
    !hasAdminPrivileges &&
    accessLevel !== coach
  );

  return (
    <ListItem>
      <ListItemAvatar className={styles.avatarContainer}>
        <TokenHolderIndicatorAvatar
          avatarClassName={styles.avatar}
          hasTokens={hasTokens}
          isBasicAvatar={!willDisplayIndicator}
          recentSpendingTier={recentSpendingTier}
          username={originalUsername}
        />
      </ListItemAvatar>
      <ListItemText className={styles.Username}>
        {accessLevel === guest ? (
          <span className={styles.nonLinkUsername}>{originalUsername}</span>
        ) : (
          <Link
            className={usernameClasses}
            href={url}
            rel="noopener noreferrer"
            target="_blank"
            onClick={handleUsernameClick}
          >
            {goldBadge}
            {fanclubBadge}
            <span className={styles.linkUsername}>{originalUsername}</span>
            {accessLevel === coach && !isFriendsOrFavoritesTab && (
              <span className={styles.coach}>
                {translate('v2profile.chat.coach')}
              </span>
            )}
            {parseInt(isRoomModerator, 10) === 1 && accessLevel !== coach && !isFriendsOrFavoritesTab && (
              <span className={modLabelWrapper}>
                {translate('v2profile.chat.mod')}
              </span>
            )}
            {accessLevel === verifiedRoomModerator && accessLevel !== coach && !isFriendsOrFavoritesTab && (
              <span className={modLabelWrapper}>
                {translate('v2profile.room.mod')}
              </span>
            )}
            {isTipLeader && (
              <Tooltip
                arrow={true}
                placement="bottom"
                title={translate('v2profile.tip.leader.title')}
              >
                <span>
                  <ImageIcon type="crown" />
                </span>
              </Tooltip>)}
          </Link>
        )}
        {ableToPeek && (
          <span className={styles.peekBadge} role="presentation" onClick={handlePeek}>
            <Tooltip arrow={true} placement="bottom" title={`${translate('profile.v2.studio.user.peek')}!`}>
              <VideocamIcon />
            </Tooltip>
          </span>
        )}
      </ListItemText>
      {isBroadcasterChat && allNotes[originalUsername] && (
        <Tooltip placement="top" title={translate('v2broadcast.edit.note')}>
          <DescriptionIcon
            className={allNotes[originalUsername] === 'red' ? styles.redNote : styles.blueNote}
            onClick={handleNoteModal}
          />
        </Tooltip>
      )}
      {canSeeContextMenu && (
        <Fragment>
          {!topTipperItem && <MoreVertIcon className={styles.ContextualMenuIcon} onClick={openContextualMenu} />}
          {additionalElement && additionalElement}
          <ContextualMenu
            ableToPeek={ableToPeek}
            anchorEl={anchorEl}
            close={closeMenu}
            handleDrawerClose={handleDrawerClose}
            handlePeek={handlePeek}
            isBroadcasterChat={isBroadcasterChat}
            isFavouritesTab={isFavouritesTab}
            isFriendsTab={isFriendsTab}
            isRoomModerator={isRoomModerator}
            isSilenced={isSilenced}
            isUserList={isUserList}
            listItemUserAccessLevel={accessLevel}
            listedUserAccessLevel={accessLevel}
            originalUsername={originalUsername}
            profileImage={profileImage}
            showContextualMenu={showMenu}
          />
        </Fragment>
      )}
    </ListItem>
  );
}

UserListItem.propTypes = {
  isStarred: PropTypes.bool.isRequired,
  originalUsername: PropTypes.string.isRequired,
  profileImage: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  accessLevel: PropTypes.string,
  additionalElement: PropTypes.node,
  handleDrawerClose: PropTypes.func,
  hasTokens: PropTypes.bool,
  isBroadcasterChat: PropTypes.bool,
  isBroadcasting: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  isFanclubSubscribed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isFavouritesTab: PropTypes.bool,
  isFriendsTab: PropTypes.bool,
  isRoomModerator: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSilenced: PropTypes.bool,
  isThePerformer: PropTypes.bool,
  isUserList: PropTypes.bool,
  recentSpendingTier: PropTypes.number,
  topTipperItem: PropTypes.bool,
};

UserListItem.defaultProps = {
  isStarred: false,
  originalUsername: '',
  profileImage: '',
  username: '',
  accessLevel: '',
  additionalElement: null,
  handleDrawerClose: null,
  hasTokens: false,
  isBroadcasterChat: false,
  isBroadcasting: '' || 0,
  isFanclubSubscribed: '' || 0,
  isFavouritesTab: false,
  isFriendsTab: false,
  isRoomModerator: '' || 0,
  isSilenced: false,
  isThePerformer: false,
  isUserList: false,
  recentSpendingTier: 0,
  topTipperItem: false,
};

export default observer(UserListItem);
